import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';
import analysisConclusionUrl from '../../../../分析结论.png';

const statCards = [
  { num: '220', label: '课程总数', sub: '参评204 · 不参评16' },
  { num: '204', label: '参评课程', sub: '全部AI智能评价' },
  { num: '8,784', label: '事实核验', sub: '观测点评价法逐条核验' },
];

const advantages = [
  { label: '案例转化', score: 81.7 },
  { label: '结构逻辑', score: 81.5 },
  { label: '价值成效', score: 80.3 },
  { label: '联系四史', score: 79.3 },
];

const weaknesses = [
  { label: '互动深度', score: 62.2 },
  { label: '互动广度', score: 66.5 },
  { label: '节奏', score: 67.2 },
  { label: '教材', score: 65.3 },
];

const factVerify = [
  { label: '真', pct: 67, color: 'bg-primary-500' },
  { label: '存疑', pct: 18, color: 'bg-accent-500' },
  { label: '假', pct: 15, color: 'bg-secondary-500' },
];

const lowChapters = [
  { label: '第一章·新时代中特社', score: 62.0 },
  { label: '导论', score: 64.0 },
  { label: '全面依法治国', score: 68.0 },
  { label: '高质量发展', score: 68.3 },
];

const highChapters = [
  { label: '国防军队', score: 74.6 },
  { label: '生态文明', score: 73.4 },
  { label: '民生', score: 73.1 },
  { label: '文化强国', score: 73.1 },
];

export default function P65OutputList({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);
  const [activeTab, setActiveTab] = useState(0);

  const tabs = ['群体画像', '事实核验', '章节地图'];

  return (
    <div className="relative min-h-screen flex flex-col py-8 md:py-10 overflow-hidden bg-background-50">
      {/* 背景光效 */}
      <div ref={orb1Ref} className="absolute top-10 right-20 w-[380px] h-[380px] rounded-full bg-primary-100/10 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-10 w-[340px] h-[340px] rounded-full bg-accent-100/8 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-5 md:mb-6">
          <a
            href={analysisConclusionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-accent-600 hover:text-accent-700 uppercase tracking-[0.15em] mb-2 rounded-full px-2 py-1 transition-all duration-700 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:ring-offset-2 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <span className="gold-dot" />AI评价分析
          </a>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-2 tracking-tight transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            首批思政课 AI 线上评价<span className="bg-gradient-to-r from-accent-500 to-primary-500 bg-clip-text text-transparent">分析结论</span>
          </h2>
          <p className={`text-sm md:text-base text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            数据基础：220门课（参评204、不参评16）· 观测点评价法 + 事实核验（8784条）
          </p>
        </div>

        {/* 数据基础三卡 */}
        <div className={`grid grid-cols-3 gap-3 md:gap-4 max-w-3xl mx-auto mb-5 md:mb-6 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {statCards.map((s, i) => (
            <div
              key={s.label}
              className="text-center p-3 md:p-4 rounded-xl glass-liquid border border-background-200/50 hover:border-primary-200/50 transition-all duration-300"
              style={{ transitionDelay: `${300 + i * 100}ms` }}
            >
              <div className="number-font text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">{s.num}</div>
              <div className="text-xs font-body font-bold text-foreground-950 mt-0.5">{s.label}</div>
              <div className="text-[11px] text-foreground-950 font-body mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Tab 切换器 */}
        <div className={`flex items-center justify-center gap-1 md:gap-2 mb-5 md:mb-6 transition-all duration-700 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {tabs.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`px-4 py-2 md:px-5 md:py-2 rounded-full text-sm font-body font-semibold whitespace-nowrap transition-all duration-300 ${
                activeTab === i
                  ? 'bg-primary-500 text-white shadow-md shadow-primary-500/20'
                  : 'glass border border-background-200/60 text-foreground-950 hover:text-foreground-950 hover:border-primary-200/50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab 内容区 */}
        <div className="max-w-4xl mx-auto">
          {activeTab === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 animate-fade-in">
              {/* 共性优势 */}
              <div className="p-5 glass-liquid border border-primary-200/30 rounded-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 border border-primary-200/40 flex items-center justify-center">
                    <i className="ri-arrow-up-line text-sm text-primary-600" />
                  </div>
                  <h4 className="text-sm font-body font-bold text-foreground-950">共性优势</h4>
                  <span className="text-xs text-foreground-950 font-body">强在"讲什么"</span>
                </div>
                <div className="space-y-3">
                  {advantages.map((a) => (
                    <div key={a.label} className="flex items-center gap-3">
                      <span className="text-sm text-foreground-950 font-body w-20">{a.label}</span>
                      <div className="flex-1 h-2.5 bg-background-200/60 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-500"
                          style={{ width: `${(a.score / 100) * 100}%` }}
                        />
                      </div>
                      <span className="number-font text-sm font-body font-bold text-primary-700 w-10 text-right">{a.score}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 共性短板 */}
              <div className="p-5 glass-liquid border border-secondary-200/30 rounded-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-secondary-100 border border-secondary-200/40 flex items-center justify-center">
                    <i className="ri-arrow-down-line text-sm text-secondary-600" />
                  </div>
                  <h4 className="text-sm font-body font-bold text-foreground-950">共性短板</h4>
                  <span className="text-xs text-foreground-950 font-body">弱在"怎么互动"</span>
                </div>
                <div className="space-y-3">
                  {weaknesses.map((w) => (
                    <div key={w.label} className="flex items-center gap-3">
                      <span className="text-sm text-foreground-950 font-body w-20">{w.label}</span>
                      <div className="flex-1 h-2.5 bg-background-200/60 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-secondary-400 to-secondary-500"
                          style={{ width: `${(w.score / 100) * 100}%` }}
                        />
                      </div>
                      <span className="number-font text-sm font-body font-bold text-secondary-700 w-10 text-right">{w.score}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-foreground-950 font-body mt-4 leading-relaxed">
                  "以教师讲授为主、真实互动匮乏"是全国性短板
                </p>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5 animate-fade-in">
              {/* 核验比例 */}
              <div className="md:col-span-5 p-5 glass-liquid border border-accent-200/30 rounded-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-accent-100 border border-accent-200/40 flex items-center justify-center">
                    <i className="ri-shield-check-line text-sm text-accent-600" />
                  </div>
                  <h4 className="text-sm font-body font-bold text-foreground-950">事实核验分布</h4>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  {factVerify.map((f) => (
                    <div key={f.label} className="text-center flex-1">
                      <div className="number-font text-2xl font-bold text-foreground-950">{f.pct}%</div>
                      <div className="text-xs text-foreground-950 font-body mt-0.5">{f.label}</div>
                    </div>
                  ))}
                </div>
                <div className="h-3 bg-background-200/60 rounded-full overflow-hidden flex">
                  {factVerify.map((f) => (
                    <div key={f.label} className={`h-full ${f.color}`} style={{ width: `${f.pct}%` }} />
                  ))}
                </div>
                <p className="text-xs text-foreground-950 font-body mt-4 leading-relaxed">
                  假 15% = 1,317条 · 重要失实 248条
                </p>
              </div>

              {/* 治理含义 */}
              <div className="md:col-span-7 p-5 glass-liquid border border-primary-200/30 rounded-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 border border-primary-200/40 flex items-center justify-center">
                    <i className="ri-lightbulb-line text-sm text-primary-600" />
                  </div>
                  <h4 className="text-sm font-body font-bold text-foreground-950">治理含义</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary-100 border border-primary-200/40 flex items-center justify-center flex-shrink-0 text-xs font-body font-bold text-primary-700">1</span>
                    <p className="text-sm text-foreground-950 font-body leading-relaxed">
                      数据/时政表述类差错是<strong className="text-primary-700">真实、大规模</strong>的教研风险，需建立核验机制
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-accent-100 border border-accent-200/40 flex items-center justify-center flex-shrink-0 text-xs font-body font-bold text-accent-700">2</span>
                    <p className="text-sm text-foreground-950 font-body leading-relaxed">
                      AI 初判<strong className="text-accent-700">必须人工复核</strong>，尤其区分"口误/转写误差 vs 真实史实错误"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 animate-fade-in">
              {/* 难点洼地 */}
              <div className="p-5 glass-liquid border border-secondary-200/30 rounded-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-secondary-100 border border-secondary-200/40 flex items-center justify-center">
                    <i className="ri-map-pin-line text-sm text-secondary-600" />
                  </div>
                  <h4 className="text-sm font-body font-bold text-foreground-950">难点洼地</h4>
                  <span className="text-xs text-foreground-950 font-body">理论/总论章</span>
                </div>
                <div className="space-y-3">
                  {lowChapters.map((c) => (
                    <div key={c.label} className="flex items-center gap-3">
                      <span className="text-sm text-foreground-950 font-body flex-1 truncate">{c.label}</span>
                      <div className="w-24 h-2.5 bg-background-200/60 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-secondary-400 to-secondary-500"
                          style={{ width: `${(c.score / 100) * 100}%` }}
                        />
                      </div>
                      <span className="number-font text-sm font-body font-bold text-secondary-700 w-10 text-right">{c.score}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-foreground-950 font-body mt-4 leading-relaxed">
                  理论抽象、难讲深透。集体备课/培训资源应向理论性章节倾斜
                </p>
              </div>

              {/* 高分区 */}
              <div className="p-5 glass-liquid border border-primary-200/30 rounded-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 border border-primary-200/40 flex items-center justify-center">
                    <i className="ri-star-line text-sm text-primary-600" />
                  </div>
                  <h4 className="text-sm font-body font-bold text-foreground-950">高分区</h4>
                  <span className="text-xs text-foreground-950 font-body">主题/案例章</span>
                </div>
                <div className="space-y-3">
                  {highChapters.map((c) => (
                    <div key={c.label} className="flex items-center gap-3">
                      <span className="text-sm text-foreground-950 font-body flex-1">{c.label}</span>
                      <div className="w-24 h-2.5 bg-background-200/60 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-500"
                          style={{ width: `${(c.score / 100) * 100}%` }}
                        />
                      </div>
                      <span className="number-font text-sm font-body font-bold text-primary-700 w-10 text-right">{c.score}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-foreground-950 font-body mt-4 leading-relaxed">
                  有抓手、易出彩。高分章节应提炼示范课，作为推广标杆
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
